// Utilities for matching an admin-curated offer against an EDOS item on
// the website. Shared between TestDetail and PackageDetail so the price
// logic stays in one place.
//
// The backend already filters to in-window + active offers, so anything
// we see here is live and should render with the discount applied.

// Find the first offer that targets `testId`. Offer.test_id is stored as
// a string; EDOS ids can arrive as number or string depending on endpoint,
// so we compare stringified versions.
export const findOfferFor = (offers, testId) => {
  if (!Array.isArray(offers) || testId === undefined || testId === null) return null;
  const key = String(testId);
  return offers.find((o) => String(o.test_id) === key) || null;
};

// Compute the discounted price for one offer. Matches the server/admin math.
export const computeFinal = (offer) => {
  if (!offer) return null;
  const base = Number(offer.original_price) || 0;
  const value = Number(offer.offer_value) || 0;
  if (offer.offer_type === 'flat') return Math.max(0, base - value);
  return Math.max(0, Math.round(base * (1 - value / 100)));
};

// Short badge text — "40% OFF" or "₹500 OFF". Used when an offer doesn't
// supply its own `badge` string.
export const shortDiscount = (offer) => {
  if (!offer) return '';
  if (offer.offer_type === 'flat') return `₹${Number(offer.offer_value).toLocaleString('en-IN')} OFF`;
  return `${Number(offer.offer_value)}% OFF`;
};

// One-call convenience: given the offers list and a test id, return the
// pricing shape detail pages want. Returns null when no offer matches,
// so callers can fall back to the plain EDOS mrp.
//
//   { offer, finalPrice, originalPrice, badge }
export const resolvePricing = (offers, testId, fallbackPrice) => {
  const offer = findOfferFor(offers, testId);
  if (!offer) return null;
  const original = Number(offer.original_price) || Number(fallbackPrice) || 0;
  const finalPrice = computeFinal({ ...offer, original_price: original });
  return {
    offer,
    originalPrice: original,
    finalPrice,
    badge: offer.badge || shortDiscount(offer),
  };
};
